import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { AppProvider } from './context/AppContext';
import LocaleProvider from './components/LocaleProvider/LocaleProvider';

// Auth Pages
import Login from './pages/Auth/Login';
import RegisterClinic from './pages/Auth/RegisterClinic';

// Main Layout
import MainLayout from './components/Layout/MainLayout';

// Dashboard
import Dashboard from './pages/Dashboard/Dashboard';

// Management Pages
import Patients from './pages/Patients/Patients';
import PatientDetail from './pages/Patients/PatientDetail';
import Dentists from './pages/Dentists/Dentists';
import Appointments from './pages/Appointments/Appointments';
import Treatments from './pages/Treatments/Treatments';
import Prescriptions from './pages/Prescriptions/Prescriptions';
import Payments from './pages/Payments/Payments';
import Expenses from './pages/Expenses/Expenses';
import Reports from './pages/Reports/Reports';
import Notifications from './pages/Notifications/Notifications';
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';

// Clinic Settings
import ClinicProfile from './pages/Clinic/ClinicProfile';
import ClinicUsers from './pages/Clinic/ClinicUsers';

// Pharmacy Pages
import PharmacyDashboard from './pages/Pharmacy/PharmacyDashboard';
import Inventory from './pages/Pharmacy/Inventory';
import Sell from './pages/Pharmacy/Sell';
import Buy from './pages/Pharmacy/Buy';
import SalesList from './pages/Pharmacy/SalesList';
import PurchasesList from './pages/Pharmacy/PurchasesList';
import Returns from './pages/Pharmacy/Returns';
import PharmacyReports from './pages/Pharmacy/PharmacyReports';

// Laboratory Pages
import LabDashboard from './pages/Laboratory/LabDashboard';
import LabOrders from './pages/Laboratory/LabOrders';
import LabReports from './pages/Laboratory/LabReports';

// Radiology Pages
import RadiologyDashboard from './pages/Radiology/RadiologyDashboard';
import RadiologyExams from './pages/Radiology/RadiologyExams';
import RadiologyReports from './pages/Radiology/RadiologyReports';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';

// Styles
import './App.css';
import './styles/rtl-utilities.css';

// Smart redirect based on role
const HomeRedirect = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role === 'super_admin') {
        return <Navigate to="/superadmin" replace />;
    }
    if (user.role === 'receptionist') {
        return <Navigate to="/appointments" replace />;
    }
    if (user.role === 'pharmacy') {
        return <Navigate to="/pharmacy" replace />;
    }
    if (user.role === 'lab_technician') {
        return <Navigate to="/laboratory" replace />;
    }
    if (user.role === 'radiologist') {
        return <Navigate to="/radiology" replace />;
    }
    return <Navigate to="/dashboard" replace />;
};

function App() {
    return (
        <AppProvider>
            <LocaleProvider>
                <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>}>
                    <Router>
                        <Routes>
                            {/* Public Routes */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<RegisterClinic />} />

                            {/* Protected Routes */}
                            <Route
                                path="/*"
                                element={
                                    <ProtectedRoute>
                                        <MainLayout>
                                            <Routes>
                                                <Route path="/" element={<HomeRedirect />} />
                                                <Route path="/dashboard" element={<Dashboard />} />
                                                <Route path="/patients" element={<Patients />} />
                                                <Route path="/patients/:id" element={<PatientDetail />} />
                                                <Route path="/dentists" element={<Dentists />} />
                                                <Route path="/appointments" element={<Appointments />} />
                                                <Route path="/treatments" element={<Treatments />} />
                                                <Route path="/notifications" element={<Notifications />} />
                                                <Route path="/prescriptions" element={<Prescriptions />} />
                                                <Route path="/payments" element={<Payments />} />
                                                <Route path="/expenses" element={<Expenses />} />
                                                <Route path="/reports" element={<Reports />} />
                                                <Route path="/superadmin" element={<SuperAdminDashboard />} />
                                                <Route path="/clinic/profile" element={<ClinicProfile />} />
                                                <Route path="/clinic/users" element={<ClinicUsers />} />

                                                {/* Pharmacy Routes */}
                                                <Route path="/pharmacy" element={<PharmacyDashboard />} />
                                                <Route path="/pharmacy/inventory" element={<Inventory />} />
                                                <Route path="/pharmacy/sell" element={<Sell />} />
                                                <Route path="/pharmacy/buy" element={<Buy />} />
                                                <Route path="/pharmacy/sales" element={<SalesList />} />
                                                <Route path="/pharmacy/purchases" element={<PurchasesList />} />
                                                <Route path="/pharmacy/returns" element={<Returns />} />
                                                <Route path="/pharmacy/reports" element={<PharmacyReports />} />

                                                {/* Laboratory Routes */}
                                                <Route path="/laboratory" element={<LabDashboard />} />
                                                <Route path="/laboratory/orders" element={<LabOrders />} />
                                                <Route path="/laboratory/reports" element={<LabReports />} />

                                                {/* Radiology Routes */}
                                                <Route path="/radiology" element={<RadiologyDashboard />} />
                                                <Route path="/radiology/exams" element={<RadiologyExams />} />
                                                <Route path="/radiology/reports" element={<RadiologyReports />} />
                                            </Routes>
                                        </MainLayout>
                                    </ProtectedRoute>
                                }
                            />
                        </Routes>
                    </Router>
                </Suspense>
            </LocaleProvider>
        </AppProvider>
    );
}

export default App;
